import { useState } from 'react';
import type { Permission } from '@markbird/shared';
import { useStore } from '../store/useStore';
import { useShare } from '../hooks/useShare';
import { useT } from '../i18n/useT';
import { Modal } from './Modal';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/**
 * Expiry choices. Without an account there is no "never": such a link is in no
 * share list, so an expiry is the only thing that can ever remove it. The
 * server enforces the same limit (`ANON_SHARE_MAX_MS`).
 */
function expiryOptions(signedIn: boolean): { key: string; ms: number | null }[] {
  const windows = [
    { key: 'shareOpt.1h', ms: HOUR },
    { key: 'shareOpt.1d', ms: DAY },
    { key: 'shareOpt.7d', ms: 7 * DAY },
    { key: 'shareOpt.30d', ms: 30 * DAY },
  ];
  return signedIn
    ? [{ key: 'shareOpt.never', ms: null }, ...windows]
    : [...windows, { key: 'shareOpt.90d', ms: 90 * DAY }];
}

/**
 * Share-with-options dialog: pick permission (read, or edit when signed in) and
 * an expiry window before creating the link. The resulting link/error surfaces
 * through the same ShareDialog as a quick share.
 */
export function ShareOptionsDialog() {
  const t = useT();
  const open = useStore((s) => s.shareOptionsOpen);
  const setOpen = useStore((s) => s.setShareOptionsOpen);
  const user = useStore((s) => s.user);
  const busy = useStore((s) => s.shareBusy);
  const { share } = useShare();
  const [permission, setPermission] = useState<Permission>('read');
  // Signed out, the choices start at an hour; the default is 30 days.
  const options = expiryOptions(!!user);
  const [expiryIdx, setExpiryIdx] = useState(() => (user ? 0 : 3));
  const expirySelection = Math.min(expiryIdx, options.length - 1);
  const [password, setPassword] = useState('');
  const [slug, setSlug] = useState('');

  if (!open) return null;

  const create = async () => {
    const ms = options[expirySelection]?.ms ?? null;
    const expiresAt = ms == null ? null : Date.now() + ms;
    const pw = password.trim();
    const cleanSlug = slug.trim().toLowerCase();
    setOpen(false);
    setPassword('');
    setSlug('');
    await share(permission, expiresAt, pw || undefined, cleanSlug || undefined);
  };

  return (
    <Modal onClose={() => setOpen(false)} title={t('shareOpt.title')}>
      <p className="mb-modal-note mb-share-explainer">{t('shareOpt.explainer')}</p>

      <label className="mb-field">
        <span>{t('shareOpt.access')}</span>
        <select value={permission} onChange={(e) => setPermission(e.target.value as Permission)}>
          <option value="read">{t('shareOpt.read')}</option>
          <option value="edit" disabled={!user}>
            {user ? t('shareOpt.edit') : t('shareOpt.editSignin')}
          </option>
        </select>
      </label>

      <label className="mb-field">
        <span>{t('shareOpt.expires')}</span>
        <select value={expirySelection} onChange={(e) => setExpiryIdx(Number(e.target.value))}>
          {options.map((o, i) => (
            <option key={o.key} value={i}>
              {t(o.key)}
            </option>
          ))}
        </select>
      </label>
      {!user && <p className="mb-modal-note">{t('shareOpt.anonExpiry')}</p>}

      <label className="mb-field">
        <span>{t('shareOpt.password')}</span>
        <input
          type="password"
          autoComplete="new-password"
          placeholder={t('shareOpt.passwordPlaceholder')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ minWidth: '12rem' }}
        />
      </label>

      <label className="mb-field">
        <span>{t('shareOpt.slug')}</span>
        <input
          type="text"
          placeholder={t('shareOpt.slugPlaceholder')}
          value={slug}
          onChange={(e) => setSlug(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())}
          style={{ minWidth: '12rem' }}
          aria-label={t('shareOpt.slug')}
        />
      </label>

      <div className="mb-modal-actions">
        <button className="mb-btn" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </button>
        <button className="mb-btn mb-btn-primary" disabled={busy} onClick={() => void create()}>
          {busy ? t('shareOpt.creating') : t('shareOpt.create')}
        </button>
      </div>
    </Modal>
  );
}
